import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError, API } from "../lib/api";
import { useI18n } from "../i18n/I18nContext";
import DashboardLayout from "../components/DashboardLayout";
import {
  Users, Pencil, FolderOpen, Upload, Download, Save, Youtube,
  MessageSquare, FileText, ChevronRight, X, Send, Trash2, Calendar,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_LABELS = {
  pending: "Pending",
  approved: "Approved",
  needs_revision: "Needs revision",
};
const STATUS_COLORS = {
  pending: "text-neutral-400 border-neutral-600 bg-neutral-900/40",
  approved: "text-emerald-300 border-emerald-700/50 bg-emerald-900/20",
  needs_revision: "text-amber-300 border-amber-700/50 bg-amber-900/20",
};

function StatusBadge({ status, small = false }) {
  const s = status || "pending";
  const cls = STATUS_COLORS[s] || STATUS_COLORS.pending;
  return (
    <span
      className={`inline-flex items-center gap-1 border ${cls} px-2 py-0.5 text-[10px] uppercase tracking-[0.16em] font-condensed`}
      style={small ? { fontSize: 10 } : undefined}
      data-testid={`sub-status-${s}`}
    >
      {STATUS_LABELS[s] || s}
    </span>
  );
}

export default function TrainerDashboard() {
  const { t } = useI18n();
  const [section, setSection] = useState("roster");

  const [roster, setRoster] = useState([]);
  const [subs, setSubs] = useState([]);
  const [resources, setResources] = useState([]);

  const [editing, setEditing] = useState(null);
  const [grade, setGrade] = useState("");
  const [feedback, setFeedback] = useState("");
  const [status, setStatus] = useState("pending");

  const [resForm, setResForm] = useState({ title: "", category: "general", file: null });
  const [videoForm, setVideoForm] = useState({ title: "", youtube_url: "", week: "", description: "" });

  const [busy, setBusy] = useState(false);
  const [drawerUid, setDrawerUid] = useState(null);

  useEffect(() => {
    if (!window.location.hash) window.location.hash = "#roster";
    const onHash = () => setSection((window.location.hash || "#roster").replace("#", ""));
    onHash();
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const reload = async () => {
    try {
      const [r, s, res] = await Promise.all([
        api.get("/trainer/roster"),
        api.get("/submissions"),
        api.get("/resources"),
      ]);
      setRoster(r.data || []);
      setSubs(s.data || []);
      setResources(res.data || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { reload(); }, []);

  const navItems = [
    { id: "roster", hash: "#roster", icon: Users, label: t("trainer.roster") },
    { id: "grading", hash: "#grading", icon: Pencil, label: t("trainer.grading") },
    { id: "resources", hash: "#resources", icon: FolderOpen, label: t("trainer.resources") },
  ];

  // ---- Grading ----
  const startGrade = (s) => {
    setEditing(s.id);
    setGrade(s.grade || "");
    setFeedback(s.feedback || "");
    setStatus(s.status || "pending");
  };
  const saveGrade = async (sid) => {
    setBusy(true);
    try {
      await api.post(`/submissions/${sid}/grade`, { grade, feedback, status });
      toast.success(t("trainer.gradeSaved", "Grade saved"));
      setEditing(null);
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  // ---- Resource: file ----
  const uploadResource = async (e) => {
    e.preventDefault();
    if (!resForm.file || !resForm.title) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("title", resForm.title);
      fd.append("category", resForm.category);
      fd.append("file", resForm.file);
      await api.post("/resources", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("trainer.resourceUploaded", "Resource uploaded"));
      setResForm({ title: "", category: "general", file: null });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  // ---- Resource: YouTube video ----
  const addVideo = async (e) => {
    e.preventDefault();
    if (!videoForm.title || !videoForm.youtube_url) return;
    setBusy(true);
    try {
      const payload = {
        title: videoForm.title.trim(),
        youtube_url: videoForm.youtube_url.trim(),
        description: videoForm.description.trim(),
      };
      const w = parseInt(videoForm.week, 10);
      if (!isNaN(w)) payload.week = w;
      await api.post("/resources/video", payload);
      toast.success(t("trainer.videoAdded", "Video added"));
      setVideoForm({ title: "", youtube_url: "", week: "", description: "" });
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const deleteResource = async (rid) => {
    if (!window.confirm("Remove this resource from the shared library?")) return;
    try {
      await api.delete(`/resources/${rid}`);
      toast.success("Removed");
      reload();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const downloadUrl = (p) => `${API}/files/download?path=${encodeURIComponent(p)}`;

  // Submission status summary for the grading tab
  const statusSummary = useMemo(() => {
    const c = { pending: 0, approved: 0, needs_revision: 0 };
    for (const s of subs) c[s.status || "pending"] = (c[s.status || "pending"] || 0) + 1;
    return c;
  }, [subs]);

  return (
    <DashboardLayout title={t("trainer.roster")} navItems={navItems}>
      {/* ---------- ROSTER (clickable rows open the Student Drawer) ---------- */}
      {section === "roster" && (
        <section data-testid="trainer-roster">
          <h2 className="font-serif text-3xl mb-6">{t("trainer.roster")}</h2>
          {roster.length === 0 ? (
            <div className="text-neutral-500 text-sm">{t("trainer.noStudents")}</div>
          ) : (
            <div className="card-imperial overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#0a0a0a] border-b border-[#1a1a1a]">
                  <tr>
                    <th className="text-left px-5 py-3 overline">Name</th>
                    <th className="text-left px-5 py-3 overline">Email</th>
                    <th className="text-left px-5 py-3 overline">Country</th>
                    <th className="text-left px-5 py-3 overline">Role</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody>
                  {roster.map((u) => (
                    <tr
                      key={u.id}
                      className="border-b border-[#141414] last:border-0 hover:bg-[#0c0c0c] transition-colors cursor-pointer"
                      data-testid={`roster-row-${u.id}`}
                      onClick={() => setDrawerUid(u.id)}
                    >
                      <td className="px-5 py-3 font-serif">{u.name}</td>
                      <td className="px-5 py-3 text-neutral-400">{u.email}</td>
                      <td className="px-5 py-3 text-neutral-400">{u.country}</td>
                      <td className="px-5 py-3"><span className="overline text-[#d4af37]">{u.role}</span></td>
                      <td className="px-5 py-3 text-right">
                        <button
                          className="inline-flex items-center gap-1 text-xs text-[#d4af37] hover:text-white"
                          data-testid={`roster-open-${u.id}`}
                          onClick={(e) => { e.stopPropagation(); setDrawerUid(u.id); }}
                        >
                          {t("trainer.openProfile", "Open profile")} <ChevronRight size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* ---------- GRADING with status + summary ---------- */}
      {section === "grading" && (
        <section data-testid="trainer-grading">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
            <h2 className="font-serif text-3xl">{t("trainer.grading")}</h2>
            <div className="flex gap-2" data-testid="grading-summary">
              <StatusBadge status="pending" />
              <span className="text-xs text-neutral-500">{statusSummary.pending}</span>
              <StatusBadge status="approved" />
              <span className="text-xs text-neutral-500">{statusSummary.approved}</span>
              <StatusBadge status="needs_revision" />
              <span className="text-xs text-neutral-500">{statusSummary.needs_revision}</span>
            </div>
          </div>
          {subs.length === 0 ? (
            <div className="text-neutral-500 text-sm">{t("trainer.noSubmissions")}</div>
          ) : (
            <div className="space-y-3">
              {subs.map((s) => (
                <div key={s.id} className="card-imperial p-5" data-testid={`sub-row-${s.id}`}>
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div className="font-serif text-lg">{s.student_name || s.student_email}</div>
                        <StatusBadge status={s.status} />
                      </div>
                      <div className="text-xs text-neutral-500">{s.original_filename}</div>
                      <div className="overline mt-1">Week {s.assignment_id.slice(0, 8)}</div>
                    </div>
                    <a
                      href={downloadUrl(s.file_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="btn-outline-gold text-xs"
                      data-testid={`sub-download-${s.id}`}
                    >
                      <Download size={12} /> {t("common.download")}
                    </a>
                    {editing === s.id ? null : (
                      <button onClick={() => startGrade(s)} className="btn-outline-gold text-xs" data-testid={`sub-grade-${s.id}`}>
                        <Pencil size={12} /> {s.grade ? t("trainer.review") : "Grade"}
                      </button>
                    )}
                  </div>
                  {editing === s.id && (
                    <div className="mt-4 grid md:grid-cols-4 gap-3" data-testid={`grade-edit-${s.id}`}>
                      <input
                        data-testid={`grade-input-${s.id}`}
                        placeholder="Grade (e.g. A, 85, Pass)"
                        value={grade}
                        onChange={(e) => setGrade(e.target.value)}
                        className="bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
                      />
                      <select
                        data-testid={`status-select-${s.id}`}
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        className="bg-[#0a0a0a] border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
                      >
                        <option value="pending">Pending</option>
                        <option value="approved">Approved</option>
                        <option value="needs_revision">Needs revision</option>
                      </select>
                      <input
                        data-testid={`feedback-input-${s.id}`}
                        placeholder="Feedback"
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                        className="md:col-span-2 bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
                      />
                      <div className="md:col-span-4 flex gap-2">
                        <button onClick={() => saveGrade(s.id)} disabled={busy} className="btn-imperial text-xs" data-testid={`save-grade-${s.id}`}>
                          <Save size={12} /> {t("trainer.saveGrade")}
                        </button>
                        <button onClick={() => setEditing(null)} className="btn-outline-gold text-xs">{t("common.cancel")}</button>
                      </div>
                    </div>
                  )}
                  {s.grade && editing !== s.id && (
                    <div className="mt-3 text-sm text-neutral-400 border-l-2 border-[#d4af37] pl-3">
                      <span className="overline mr-2">Grade</span>{s.grade}
                      {s.feedback && <><br /><span className="overline mr-2">Feedback</span>{s.feedback}</>}
                      {s.graded_by_name && <><br /><span className="overline mr-2">Graded by</span>{s.graded_by_name}</>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ---------- SHARED RESOURCE MANAGER (files + YouTube + week mapping) ---------- */}
      {section === "resources" && (
        <section data-testid="trainer-resources">
          <h2 className="font-serif text-3xl mb-6">{t("trainer.resources")}</h2>

          {/* File upload form (existing) */}
          <form onSubmit={uploadResource} className="card-imperial p-5 mb-4 grid md:grid-cols-4 gap-3" data-testid="resource-upload-form">
            <div className="md:col-span-4 overline text-[#d4af37] mb-1 flex items-center gap-2">
              <FileText size={12} /> {t("trainer.uploadDocs", "Upload document (PDF, template…)")}
            </div>
            <input required data-testid="resource-title" placeholder={t("trainer.title")} value={resForm.title}
              onChange={(e) => setResForm({ ...resForm, title: e.target.value })}
              className="bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm" />
            <input data-testid="resource-category" placeholder={t("trainer.category")} value={resForm.category}
              onChange={(e) => setResForm({ ...resForm, category: e.target.value })}
              className="bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm" />
            <label className="border border-[#222] hover:border-[#d4af37] cursor-pointer px-3 py-2 text-sm flex items-center gap-2 text-neutral-400 transition-colors">
              <Upload size={14} /> {resForm.file ? resForm.file.name : t("trainer.file")}
              <input type="file" data-testid="resource-file" className="hidden" onChange={(e) => setResForm({ ...resForm, file: e.target.files?.[0] })} />
            </label>
            <button type="submit" disabled={busy} className="btn-imperial text-xs" data-testid="resource-submit">
              {busy ? t("common.loading") : t("trainer.uploadResource")}
            </button>
          </form>

          {/* YouTube video form */}
          <form onSubmit={addVideo} className="card-imperial p-5 mb-6 grid md:grid-cols-5 gap-3" data-testid="video-upload-form">
            <div className="md:col-span-5 overline text-[#e6192b] mb-1 flex items-center gap-2">
              <Youtube size={12} /> {t("trainer.addVideo", "Paste a YouTube link · map to a curriculum week")}
            </div>
            <input
              required
              data-testid="video-title"
              placeholder={t("trainer.title")}
              value={videoForm.title}
              onChange={(e) => setVideoForm({ ...videoForm, title: e.target.value })}
              className="md:col-span-2 bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
            />
            <input
              required
              data-testid="video-url"
              placeholder="https://youtube.com/watch?v=…"
              value={videoForm.youtube_url}
              onChange={(e) => setVideoForm({ ...videoForm, youtube_url: e.target.value })}
              className="md:col-span-2 bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
            />
            <select
              data-testid="video-week"
              value={videoForm.week}
              onChange={(e) => setVideoForm({ ...videoForm, week: e.target.value })}
              className="bg-[#0a0a0a] border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
            >
              <option value="">{t("trainer.noWeek", "No week")}</option>
              {[1,2,3,4,5,6,7,8,9,10,11,12].map((w) => (
                <option key={w} value={w}>Week {w}</option>
              ))}
            </select>
            <input
              data-testid="video-desc"
              placeholder={t("trainer.descPlaceholder", "Short description (optional)")}
              value={videoForm.description}
              onChange={(e) => setVideoForm({ ...videoForm, description: e.target.value })}
              className="md:col-span-4 bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
            />
            <button type="submit" disabled={busy} className="btn-imperial text-xs" data-testid="video-submit">
              {busy ? t("common.loading") : t("trainer.addVideo", "Add video")}
            </button>
          </form>

          {/* Library — videos first, then files */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {resources.map((r) => (
              <div key={r.id} className="card-imperial p-5 relative group" data-testid={`tres-${r.id}`}>
                <button
                  className="absolute top-2 right-2 text-neutral-600 hover:text-[#e6192b] opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => deleteResource(r.id)}
                  title="Remove"
                  data-testid={`tres-delete-${r.id}`}
                >
                  <Trash2 size={14} />
                </button>
                {r.kind === "video" ? (
                  <>
                    <a href={r.youtube_url} target="_blank" rel="noreferrer" className="block">
                      <div className="aspect-video w-full bg-[#0a0a0a] border border-[#1a1a1a] mb-3 relative overflow-hidden">
                        {r.thumbnail && (
                          <img src={r.thumbnail} alt={r.title} className="w-full h-full object-cover opacity-90" />
                        )}
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <Youtube size={36} className="text-[#e6192b]" />
                        </div>
                      </div>
                    </a>
                    <div className="font-serif text-lg leading-tight">{r.title}</div>
                    <div className="flex items-center gap-2 mt-1">
                      {r.week ? (
                        <span className="overline text-[#d4af37]">Week {r.week}</span>
                      ) : (
                        <span className="overline text-neutral-500">No week</span>
                      )}
                    </div>
                    {r.description && <div className="text-xs text-neutral-500 mt-2 line-clamp-2">{r.description}</div>}
                    <a href={r.youtube_url} target="_blank" rel="noreferrer" className="mt-3 btn-outline-gold w-full text-xs">
                      <Youtube size={12} /> {t("trainer.watch", "Watch on YouTube")}
                    </a>
                  </>
                ) : (
                  <>
                    <div className="font-serif text-lg">{r.title}</div>
                    <div className="overline mt-1">{r.category}</div>
                    <div className="text-xs text-neutral-500 mt-2 truncate">{r.original_filename}</div>
                    <a href={downloadUrl(r.file_path)} target="_blank" rel="noreferrer" className="mt-3 btn-outline-gold w-full text-xs">
                      <Download size={12} /> {t("common.download")}
                    </a>
                  </>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      {drawerUid && <StudentDrawer uid={drawerUid} onClose={() => setDrawerUid(null)} downloadUrl={downloadUrl} />}
    </DashboardLayout>
  );
}

// =========================================================================
// Student Drawer — full profile + submissions + internal trainer notes
// =========================================================================
function StudentDrawer({ uid, onClose, downloadUrl }) {
  const { t } = useI18n();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [posting, setPosting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/trainer/students/${uid}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      onClose();
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [uid]);

  const postNote = async (e) => {
    e.preventDefault();
    const txt = noteText.trim();
    if (!txt) return;
    setPosting(true);
    try {
      await api.post(`/trainer/students/${uid}/notes`, { note: txt });
      setNoteText("");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setPosting(false); }
  };

  const deleteNote = async (nid) => {
    if (!window.confirm("Delete this internal note?")) return;
    try {
      await api.delete(`/trainer/notes/${nid}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" data-testid="student-drawer">
      <div className="flex-1 bg-black/70" onClick={onClose} />
      <div className="w-full md:w-[640px] bg-[#080808] border-l border-[#1a1a1a] overflow-y-auto shadow-2xl">
        <div className="sticky top-0 bg-[#080808] border-b border-[#1a1a1a] px-6 py-4 flex items-center justify-between z-10">
          <div className="overline text-[#d4af37]">{t("trainer.studentProfile", "Student profile")}</div>
          <button onClick={onClose} className="text-neutral-400 hover:text-white" data-testid="drawer-close">
            <X size={20} />
          </button>
        </div>

        {loading || !data ? (
          <div className="p-8 text-neutral-500 text-sm">{t("common.loading")}…</div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Profile header */}
            <div>
              <div className="font-serif text-3xl">{data.student.name}</div>
              <div className="text-sm text-neutral-400">{data.student.email}</div>
              <div className="flex flex-wrap gap-3 mt-3 text-xs">
                <span className="border border-[#1a1a1a] px-2 py-0.5 overline text-[#d4af37]">{data.student.role}</span>
                {data.student.country && <span className="text-neutral-500">{data.student.country}</span>}
                {data.enrollment && (
                  <span className={`border px-2 py-0.5 overline ${data.enrollment.payment_status === "paid" ? "border-emerald-700/50 text-emerald-300" : "border-amber-700/50 text-amber-300"}`}>
                    {data.enrollment.tier || ""} · {data.enrollment.payment_status}
                  </span>
                )}
              </div>
            </div>

            {/* Submissions */}
            <div>
              <div className="overline text-neutral-500 mb-3">{t("trainer.submissions", "Submissions")}</div>
              {data.submissions.length === 0 ? (
                <div className="text-sm text-neutral-500">{t("trainer.noSubmissions")}</div>
              ) : (
                <div className="space-y-2">
                  {data.submissions.map((s) => (
                    <div key={s.id} className="border border-[#1a1a1a] bg-[#0a0a0a] p-3 text-sm" data-testid={`drawer-sub-${s.id}`}>
                      <div className="flex items-center justify-between gap-3">
                        <div className="truncate">{s.original_filename}</div>
                        <StatusBadge status={s.status} small />
                      </div>
                      <div className="flex items-center justify-between mt-1 text-xs text-neutral-500">
                        <span>{s.grade ? `Grade: ${s.grade}` : "— ungraded —"}</span>
                        <a href={downloadUrl(s.file_path)} target="_blank" rel="noreferrer" className="text-[#d4af37] hover:text-white inline-flex items-center gap-1">
                          <Download size={11} /> {t("common.download")}
                        </a>
                      </div>
                      {s.feedback && <div className="mt-2 text-xs text-neutral-400 border-l-2 border-[#d4af37] pl-2">{s.feedback}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Internal trainer notes */}
            <div>
              <div className="overline text-[#d4af37] mb-3 flex items-center gap-2">
                <MessageSquare size={12} /> {t("trainer.internalNotes", "Internal trainer notes")}
              </div>
              <form onSubmit={postNote} className="flex flex-col gap-2 mb-4" data-testid="note-form">
                <textarea
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  placeholder={t("trainer.notePlaceholder", "e.g. Needs extra help with database setups")}
                  rows={3}
                  className="bg-transparent border border-[#222] focus:border-[#d4af37] outline-none px-3 py-2 text-sm"
                  data-testid="note-input"
                />
                <button type="submit" disabled={posting || !noteText.trim()} className="btn-imperial self-end text-xs" data-testid="note-submit">
                  <Send size={12} /> {posting ? t("common.loading") : t("trainer.postNote", "Post note")}
                </button>
              </form>
              {data.notes.length === 0 ? (
                <div className="text-sm text-neutral-500">{t("trainer.noNotes", "No notes yet. Start the conversation with your colleagues.")}</div>
              ) : (
                <div className="space-y-2">
                  {data.notes.map((n) => (
                    <div key={n.id} className="border border-[#1a1a1a] bg-[#0a0a0a] p-3 text-sm" data-testid={`note-${n.id}`}>
                      <div className="flex items-center justify-between text-xs">
                        <div>
                          <span className="text-[#d4af37] font-condensed tracking-wider uppercase">{n.trainer_name}</span>
                          <span className="text-neutral-600 ml-2 inline-flex items-center gap-1">
                            <Calendar size={10} /> {new Date(n.created_at).toLocaleString()}
                          </span>
                        </div>
                        <button onClick={() => deleteNote(n.id)} className="text-neutral-600 hover:text-[#e6192b]" data-testid={`note-delete-${n.id}`}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <div className="mt-1 text-neutral-300 whitespace-pre-line">{n.note}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
